import React, { useEffect, useState } from "react";
import "./AddComponent.css";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import Seo from "../Components/Seo";
import { authedFetch } from "../services/apiClient";

const MAX_IMAGES = 6;

export default function AddProduct() {
  const navigate = useNavigate();
  /* ---------------- FORM ---------------- */
  const [productName, setProductName] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [pinCode, setPinCode] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");

  /* ---------------- LOCATION ---------------- */
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedCityId, setSelectedCityId] = useState("");

  /* ---------------- CATEGORY ---------------- */
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");

  /* ---------------- MEDIA ---------------- */
  const [images, setImages] = useState([]);

  /* ---------------- UI ---------------- */
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const mobileNo = localStorage.getItem("userMobileNumber") || "";
  // const mobileNo = "8899313233";



  /* ---------------- HELPERS ---------------- */
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  /* ---------------- FETCH INIT ---------------- */
  useEffect(() => {

    if (!mobileNo) {
      navigate("/login");
      return;
    }
    fetchStates();
    fetchCategories();
  }, [mobileNo, navigate]);

  const fetchStates = async () => {
    const res = await fetch(`${API_BASE_URL}/State/GetStates`);
    setStates(await res.json());
  };

  const fetchCities = async (id) => {
    const res = await fetch(
      `${API_BASE_URL}/City/GetCitiesByState?id=${id}`
    );
    setCities(await res.json());
  };

  const fetchCategories = async () => {
    const res = await fetch(
      `${API_BASE_URL}/Category/GetCategories`
    );
    setCategories(await res.json());
  };

  const fetchSubCategories = async (id) => {
    const res = await fetch(
      `${API_BASE_URL}/SubCategory/GetSubCategoriesByCategory?id=${id}`
    );
    setSubCategories(await res.json());
  };

  /* ---------------- PINCODE ---------------- */
  const handlePincodeBlur = () => {
    if (pinCode.length !== 6) return;
  };

  /* ---------------- IMAGES ---------------- */
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > MAX_IMAGES) return;
    setImages((prev) => [...prev, ...files]);
  };

  const removeImage = (i) =>
    setImages(images.filter((_, index) => index !== i));

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async () => {
    setError("");

    if (!mobileNo) {
      alert("Login required");
      return;
    }

    if (!productName) {
      setError("Please enter product name");
      return;
    }

    if (!price) {
      setError("Please enter price");
      return;
    }

    // Prevent mobile numbers in text
    const mobilePattern = /\b\d{10}\b/;
    if (mobilePattern.test(description) || mobilePattern.test(address)) {
      setError("Do not add mobile number in description or address");
      return;
    }

    setLoading(true);
    setProgress(10);

    try {
      // ✅ PAYLOAD MUST BE ROOT LEVEL (NO model)
      const payload = {
        Id: "0",
        MobileNo: String(mobileNo), // 🔥 IMPORTANT
        ProductName: productName,
        Price: Number(price),
        Quantity: Number(quantity) || 1,
        PinCode: pinCode ? Number(pinCode) : 0,

        State: selectedStateId ? Number(selectedStateId) : null,
        City: selectedCityId ? Number(selectedCityId) : null,
        Category: selectedCategoryId ? Number(selectedCategoryId) : null,
        SubCategory: selectedSubCategoryId ? Number(selectedSubCategoryId) : null,

        Address: address || "",
        Description: description || "",

        Image1: null,
        Image2: null,
        Image3: null,
        Image4: null,
        Image5: null,
        Image6: null,
        Video: null,
      };

      // Images → base64
      for (let i = 0; i < images.length; i++) {
        payload[`Image${i + 1}`] = await fileToBase64(images[i]);
        setProgress(20 + i * 10);
      }

      console.log("FINAL PAYLOAD (CORRECT):", payload);

      const response = await authedFetch(
        `${API_BASE_URL}/product/AddProduct`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload), // ✅ NO model wrapper
        }
      );

      if (!response.ok) {
        const err = await response.json();
        console.error("Backend error:", err);
        throw new Error("Upload failed");
      }

      setProgress(100);
      alert("Product uploaded successfully!");
      window.location.href = "/myadds";

    } catch (err) {
      console.error(err);
      setError("Product upload failed");
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };



  /* ---------------- UI ---------------- */
  return (
    <div className="add-product-page">
      <Seo title="Post Ad — Free Product Upload" description="AIO India pe 100% free apna product post karein — Mobile, Electronics, Car, Furniture, job ya kuch bhi. Aasani se lakhon buyers tak pahunchein." path="/AddProduct" />
      <div className="card">
        <h2>Add Product</h2>

        {error && <p className="error">{error}</p>}

        {/* GRID FORM */}
        <div className="form-grid">
          <input
            className="form-input"
            placeholder="Product Name"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
          />

          <input
            className="form-input"
            placeholder="Price"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />

          <input
            className="form-input"
            placeholder="Quantity"
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />

          <input
            className="form-input"
            placeholder="Pincode"
            value={pinCode}
            maxLength={6}
            onChange={(e) => setPinCode(e.target.value.replace(/\D/g, ""))}
            onBlur={handlePincodeBlur}
          />

          <select
            className="form-input"
            onChange={(e) => {
              console.log("Selected state ID:", e.target.value);
              setSelectedStateId(e.target.value);
              fetchCities(e.target.value);
            }}
          >
            <option value="">Select State</option>
            {states.map((s) => (
              <option key={s.Id ?? s.id} value={s.Id ?? s.id}>
                {s.Name ?? s.name}
              </option>
            ))}
          </select>

          <select
            className="form-input"
            onChange={(e) => setSelectedCityId(e.target.value)}
          >
            <option value="">Select City</option>
            {cities.map((c) => (
              <option key={c.Id ?? c.id} value={c.Id ?? c.id}>
                {c.Name ?? c.name}
              </option>
            ))}
          </select>

          <select
            className="form-input"
            onChange={(e) => {
              setSelectedCategoryId(e.target.value);
              fetchSubCategories(e.target.value);
            }}
          >
            <option value="">Select Category</option>
            {categories.map((c) => (
              <option key={c.Id ?? c.id} value={c.Id ?? c.id}>
                {c.Name ?? c.name}
              </option>
            ))}
          </select>

          <select
            className="form-input"
            onChange={(e) => setSelectedSubCategoryId(e.target.value)}
          >
            <option value="">Select Sub-Category</option>
            {subCategories.map((sc) => (
              <option key={sc.Id ?? sc.id} value={sc.Id ?? sc.id}>
                {sc.Name ?? sc.name}
              </option>
            ))}
          </select>
        </div>

        <textarea
          className="form-textarea"
          placeholder="Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
        />

        <textarea
          className="form-textarea"
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        {/* MEDIA */}
        <div className="media-section">
          <h4>Images (max 6)</h4>
          <input
            className="file-input"
            type="file"
            multiple
            accept="image/*"
            onChange={handleImageChange}
          />

          <div className="image-grid">
            {images.map((img, i) => (
              <div key={i} className="image-box">
                <img src={URL.createObjectURL(img)} alt="" />
                <button onClick={() => removeImage(i)}>✕</button>
              </div>
            ))}
          </div>
        </div>

        {loading && (
          <div className="progress">
            Uploading… {progress}%
            <div className="bar">
              <div style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <button
          className="submit-btn"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Uploading..." : "Post Product"}
        </button>
      </div>
    </div>
  );
}
